import { ApolloClient, CombinedGraphQLErrors, HttpLink, InMemoryCache, from } from "@apollo/client";
import { SetContextLink } from "@apollo/client/link/context";
import { ErrorLink } from "@apollo/client/link/error";
import type { DocumentNode } from "graphql";

import { baseURL } from "../utils/urls";

const authLink = new SetContextLink((prevContext) => {
  const token = localStorage.getItem("jwtToken");
  const headers = { ...(prevContext.headers as Record<string, string> | undefined) };
  if (token) headers.Authorization = `Bearer ${token}`;
  return { headers };
});

const errorLink = new ErrorLink(({ error }) => {
  if (!CombinedGraphQLErrors.is(error)) return;
  const rejected = error.errors.some((graphQLError) => {
    const code = graphQLError.extensions?.code;
    return code === "UNAUTHENTICATED" || graphQLError.message === "jwt expired" || graphQLError.message === "Unauthorized";
  });
  if (!rejected) return;
  localStorage.removeItem("jwtToken");
  window.dispatchEvent(new Event("venturify-session-rejected"));
});

export const apolloClient = new ApolloClient({
  link: from([
    errorLink,
    authLink,
    new HttpLink({ uri: `${baseURL}/graphql` }),
  ]),
  cache: new InMemoryCache(),
});

export async function mutate<T>(
  mutation: DocumentNode,
  variables: Record<string, unknown> | undefined,
  refetchQueries: string[],
): Promise<T> {
  try {
    const result = await apolloClient.mutate<T>({
      mutation,
      variables,
      refetchQueries,
      awaitRefetchQueries: true,
    });
    if (!result.data) throw new Error("Request failed");
    return result.data;
  } catch (error) {
    if (CombinedGraphQLErrors.is(error)) {
      throw new Error(error.errors[0]?.message || "Request failed");
    }
    throw error instanceof Error ? error : new Error("Request failed");
  }
}
